import { db, storage } from "@/lib/firebase";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";

const MEDIA_COLLECTION = "media";
const USAGE_COLLECTION = "mediaUsage";

export async function trackMediaUsage(mediaUrlOrId: string, entityType: string, entityId: string) {
  await addDoc(collection(db, USAGE_COLLECTION), {
    mediaId: mediaUrlOrId,
    entityType,
    entityId,
    timestamp: serverTimestamp(),
  });
}

export async function untrackMediaUsage(mediaUrlOrId: string, entityType: string, entityId: string) {
  const q = query(
    collection(db, USAGE_COLLECTION),
    where("mediaId", "==", mediaUrlOrId),
    where("entityType", "==", entityType),
    where("entityId", "==", entityId)
  );
  const snapshot = await getDocs(q);
  await Promise.all(snapshot.docs.map((d) => deleteDoc(doc(db, USAGE_COLLECTION, d.id))));
}

export async function deleteMedia(mediaId: string, url?: string) {
  // Delete from Firestore
  await deleteDoc(doc(db, MEDIA_COLLECTION, mediaId));
  
  // Delete from Storage if URL is provided
  if (url && url.includes("firebasestorage.googleapis.com")) {
    try {
      const storageRef = ref(storage, url);
      await deleteObject(storageRef);
    } catch (error) {
      console.error("Failed to delete file from storage:", error);
    }
  }
}

export async function deleteMediaPermanently(mediaId: string, url: string) {
  if (!url) return;

  const collectionsToClean = ["courses", "articles", "magazines", "pyq", "staff", "testimonials"];
  
  // 1. Scan and remove references from all collections
  for (const colName of collectionsToClean) {
    try {
      const q = query(collection(db, colName));
      const snap = await getDocs(q);
      
      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        let needsUpdate = false;
        const updatePayload: any = {};

        if (colName === "courses" && data.image === url) {
          updatePayload.image = "";
          needsUpdate = true;
        }
        if (colName === "articles" && data.image === url) {
          updatePayload.image = "";
          needsUpdate = true;
        }
        if (colName === "magazines") {
          if (data.imageUrl === url) { updatePayload.imageUrl = ""; needsUpdate = true; }
          if (data.pdfUrl === url) { updatePayload.pdfUrl = ""; needsUpdate = true; }
        }
        if (colName === "pyq" && data.documentUrls?.includes(url)) {
          updatePayload.documentUrls = data.documentUrls.filter((u: string) => u !== url);
          needsUpdate = true;
        }
        if (colName === "staff" && data.imageUrl === url) {
          updatePayload.imageUrl = "";
          needsUpdate = true;
        }
        if (colName === "testimonials" && data.imageUrl === url) {
          updatePayload.imageUrl = "";
          needsUpdate = true;
        }

        if (needsUpdate) {
          await updateDoc(doc(db, colName, docSnap.id), updatePayload);
        }
      }
    } catch (e) {
      console.error(`Failed to clean collection ${colName}:`, e);
    }
  }

  // 2. Check and remove from faculty poster
  try {
    const posterSnap = await getDoc(doc(db, "settings", "faculty_poster"));
    if (posterSnap.exists() && posterSnap.data().url === url) {
      await updateDoc(doc(db, "settings", "faculty_poster"), { url: "" });
    }
  } catch (e) {}

  // 3. Delete from Media collection and Storage
  await deleteMedia(mediaId, url);
}
